import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/app-layout";
import { ChoreHistoryItem, listChoreHistory, listUsers } from "@repo/api/client";
import { Button } from "@/components/ui/button";
import { History, SquareArrowOutUpLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EventPropagationBoundary } from "@/components/event-propagation-boundary";
import { authClient } from "@/lib/auth-client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 20;

export function ChoreHistoryPage() {
  const { data: session } = authClient.useSession();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const effectiveUserId = selectedUserId ?? session?.user.id;

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: listUsers,
  });

  const historyQuery = useInfiniteQuery({
    queryKey: ["chore-history", effectiveUserId],
    queryFn: ({ pageParam }) =>
      listChoreHistory({
        userId: effectiveUserId,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_SIZE ? undefined : allPages.length * PAGE_SIZE,
    enabled: effectiveUserId !== undefined,
  });

  const items = historyQuery.data?.pages.flat() ?? [];
  const { isPending, isFetchingNextPage, hasNextPage } = historyQuery;

  return (
    <AppLayout title="History">
      <div className="px-4 pt-6 space-y-4">
        <Select value={effectiveUserId ?? ""} onValueChange={(id) => setSelectedUserId(id)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a user" />
          </SelectTrigger>
          <SelectContent>
            {usersQuery.data?.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.email}
                {u.id === session?.user.id && <span className="text-muted-foreground"> (you)</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {historyQuery.isError && (
          <p className="text-sm text-destructive">Failed to load chore history.</p>
        )}
        {!isPending && !historyQuery.isError && items.length === 0 && (
          <p className="text-sm text-muted-foreground">No chore completions yet.</p>
        )}
        {isPending ? (
          <HistoryRowSkeletons />
        ) : (
          <ul className="space-y-1">
            {items.map((item) => (
              <ChoreHistoryListItem key={item.completionId} item={item} />
            ))}
          </ul>
        )}
        {isFetchingNextPage && <HistoryRowSkeletons />}
        {(isPending || hasNextPage) && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={isPending || isFetchingNextPage}
            onClick={() => historyQuery.fetchNextPage()}
          >
            Load more
          </Button>
        )}
      </div>
    </AppLayout>
  );
}

function ChoreHistoryListItem({ item }: { item: ChoreHistoryItem }) {
  const navigate = useNavigate();

  const completedAt = item.completedAt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const actualDuration = item.actualDurationMins != null ? `${item.actualDurationMins} min` : "—";
  const expectedDuration =
    item.expectedDurationMins != null ? `${item.expectedDurationMins} min` : "—";

  const choreUrl = `/chore-schedules/${item.choreScheduleId}/chores/${item.choreId}`;

  return (
    <Button
      asChild
      variant="outline"
      className="w-full h-auto py-2"
      onClick={() => navigate(choreUrl)}
      onKeyDown={(e) => e.key === "Enter" && navigate(choreUrl)}
    >
      <div data-href={choreUrl} tabIndex={0} role="link">
        <History size={20} className="shrink-0 text-muted-foreground" />
        <span className="flex-1 flex flex-col items-start min-w-0">
          <span className="font-medium leading-tight">{item.choreName}</span>
          <span className="text-xs text-muted-foreground leading-tight">{completedAt}</span>
        </span>
        <span className="text-xs text-muted-foreground text-right">
          {actualDuration} / {expectedDuration}
        </span>
        {item.completionGoogleCalendarHtmlLink != null && (
          <EventPropagationBoundary filter={{ onClick: true, onKeyDown: true }}>
            <Link to={item.completionGoogleCalendarHtmlLink} target="_blank">
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <SquareArrowOutUpLeft size={14} className="-scale-x-100" />
              </Button>
            </Link>
          </EventPropagationBoundary>
        )}
      </div>
    </Button>
  );
}

function HistoryRowSkeletons() {
  return (
    <div className="space-y-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}
